import type { StripeCardElement } from '@stripe/stripe-js'
import { type ComputedRef, ref, watch } from 'vue'

import {
  type CpmFormValues,
  type CpmValues,
  normalizeAdvertiserOrderSalesPixel,
  type SalesPixelSettings,
  serializeAdvertiserCpms,
} from '@/lib/adapters/advertiser.adapter'
import { handleErrorMessage } from '@/lib/helpers'
import { useApi } from '@/lib/helpers/api.helpers'
import { stripe } from '@/lib/helpers/stripe'
import { request } from '@/lib/services'
import type { AdvertiserType } from '@/lib/types'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useErrorStore } from '@/stores/error.stores'

type Collector = 'woocommerce' | 'shopify'

export type OrderCollectorSettings = {
  activationKey: string
  latestOrderAt?: string
  latestOrderValue?: number
  testOrderUrl: string
}

export type UpdateAdvertiserPayload = {
  domains?: string[]
  categories?: number[]
}

const normalizeAdvertiserOrderCollector = (data: {
  activation_key: string
  latest_order_at?: string
  order_value?: number
  test_order_url: string
}): OrderCollectorSettings => ({
  activationKey: data.activation_key,
  latestOrderAt: data.latest_order_at,
  latestOrderValue: data.order_value,
  testOrderUrl: data.test_order_url,
})

export const useGetOrderCollectorSettings = (
  advertiser: ComputedRef<AdvertiserType | undefined>,
  collector: Collector,
) => {
  const errorStore = useErrorStore()
  const error = ref<Error | null>(null)
  const isLoading = ref(false)
  const data = ref<OrderCollectorSettings | null>(null)

  const getOrderCollectors = async (advertiserId: number) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await request.get(
        `/v2/advertisers/${advertiserId}/order-collectors/${collector}`,
      )

      data.value = normalizeAdvertiserOrderCollector(response.data)
    } catch (e: unknown) {
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    } finally {
      isLoading.value = false
    }
  }

  watch(
    () => advertiser?.value,
    async () => {
      const advertiserId = advertiser?.value?.id
      if (advertiserId) {
        await getOrderCollectors(advertiserId)
      }
    },
    { immediate: true },
  )

  return { data }
}

export const useGetSalesPixelSettings = (advertiser: ComputedRef<AdvertiserType | undefined>) => {
  const errorStore = useErrorStore()
  const error = ref<Error | null>(null)
  const isLoading = ref(false)
  const data = ref<SalesPixelSettings | null>(null)

  const getSalesPixelSettings = async (advertiserId: number) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await request.get(
        `/v2/advertisers/${advertiserId}/order-collectors/sales-pixel`,
      )

      data.value = normalizeAdvertiserOrderSalesPixel(response.data)
    } catch (e: unknown) {
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    } finally {
      isLoading.value = false
    }
  }

  watch(
    () => advertiser?.value,
    async () => {
      const advertiserId = advertiser?.value?.id
      if (advertiserId) {
        await getSalesPixelSettings(advertiserId)
      }
    },
    { immediate: true },
  )

  return { data }
}

export const useUpdateAdvertiser = (storeId: ComputedRef<string>) => {
  return useApi({
    updateAdvertiser: (data: UpdateAdvertiserPayload) =>
      request.patch(`/advertiser/stores/${storeId.value}`, data),
  })
}

export const useUpdatePaymentCard = (advertiserId: ComputedRef<string>) => {
  const store = useAdvertiserStore()
  return useApi({
    updateCard: async (card: StripeCardElement) => {
      const { data } = await request.get<{ intent?: { client_secret?: string } }>(
        `/advertiser/${advertiserId.value}/stripe/session`,
      )

      const clientSecret = data.intent?.client_secret
      if (!clientSecret) {
        throw new Error(
          'Unable to update card: missing Stripe client secret from session endpoint.',
        )
      }

      let confirmResult: { error?: { message?: string } } | undefined
      try {
        confirmResult = await stripe.confirmCardSetup(clientSecret, {
          payment_method: { card },
        })
      } catch {
        throw new Error('Unable to update card: Stripe is not initialized.')
      }

      if (confirmResult.error) {
        throw new Error(
          confirmResult.error.message ||
            'Unable to update card. Please check your card details and try again.',
        )
      }

      await store.fetchAdvertiser(Number(advertiserId.value))
    },
  })
}

export const useUploadAdvertiserLogo = (advertiserId: ComputedRef<string>) => {
  const store = useAdvertiserStore()
  return useApi({
    uploadLogo: async (logo: File | Blob) => {
      const formData = new FormData()
      formData.append('logo', logo)

      await request.post(`/advertiser/stores/${advertiserId.value}/upload-logo`, formData, {
        isFormData: true,
      })

      await store.fetchAdvertiser(Number(advertiserId.value))
    },
  })
}

export const useAdvertiserApi = () => {
  const store = useAdvertiserStore()
  return useApi({
    deactivate: async (id: number): Promise<void> => {
      await request.post(`/v2/advertisers/${id}/deactivate`, {})
      await store.fetchAdvertiser(id)
    },
  })
}

export const useUpdateCpms = (advertiserId: ComputedRef<string>) => {
  return useApi({
    updateCpms: async (values: CpmFormValues, originalCpms?: CpmValues) =>
      request.patch(
        `/v2/advertisers/${advertiserId.value}/cpms`,
        serializeAdvertiserCpms(values, originalCpms),
      ),
  })
}
